import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Common from "us.common";
import { connect, ConnectedProps } from "react-redux";

import { getMIMETypeByFileFormat } from "./Functions";
import { RootState } from "us.helper/types";
import { FileExclamationOutlined } from "us.icons";
import { $Empty } from "us.common/components";

const { $Button, $Skeleton } = Common.Components;

type AssignDocContentProps = {
  onClose: () => void;
};
/**
@description
AssignDocContent Component
Drawer content that previews assigned document content for mapped documents.
Handles decoding base64 content, generating blob URLs for preview, and rendering
PDF/image/object previews with loading and error states.
Features:
- Decode base64 content to blob and create object URL
- Show skeleton while loading and fallback when preview unavailable
- Support for multiple document types via MIME mapping
@link https://unicorn-solutions.atlassian.net/browse/UPD-20
@author Keshan Nimnaka <keshana@unicorn-solutions.com>
@since 29/06/2026
*/
const AssignDocContent: React.FC<AssignDocContentProps & PropsFromRedux> = (
  props,
) => {
  const { t } = useTranslation();
  const { onClose, assignedDocumentContent } = props;

  const content = (assignedDocumentContent as any)?.data?.content ?? "";
  const docType = (assignedDocumentContent as any)?.data?.documentType ?? "";
  const mimeType = getMIMETypeByFileFormat(docType);

  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);

  const urlRef = useRef<string>("");

  useEffect(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = "";
    }

    setPreviewUrl("");

    if (!content) return;

    const safeContent = String(content).replace(/\s/g, "");
    const dataUri = `data:${mimeType};base64,${safeContent}`;

    let mounted = true;
    setIsPreviewLoading(true);

    (async () => {
      try {
        const resp = await fetch(dataUri);
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);

        if (!mounted) return;

        urlRef.current = url;
        setPreviewUrl(url);
        setIsPreviewLoading(false);
      } catch (error) {
        if (!mounted) return;

        setPreviewUrl("");
        setIsPreviewLoading(false);
      }
    })();

    return () => {
      mounted = false;

      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = "";
      }
    };
  }, [content, mimeType]);

  const isFetching = (assignedDocumentContent as any)?.isFetching;
  const showError = !isFetching && (!content || content.length === 0);

  const fallbackUrl = `data:${mimeType};base64,${content}`;
  const finalUrl = previewUrl || fallbackUrl;

  return (
    <>
      <$Skeleton loading={assignedDocumentContent.isFetching}>
        {docType !== "xml" &&
          (showError ? (
            <div className="unable-to-display">
              <$Empty
                image={<FileExclamationOutlined />}
                description={
                  <span>
                    {t(
                      "US.COLLECTION.DOCUMENTS:CASEDOCUMENTS.PREVIEW_IS_NOT_AVAILABLE",
                    )}
                  </span>
                }
              />
            </div>
          ) : content && content.length > 0 ? (
            <div className="case-doc-viwer-inner">
              <div className="mapdoc-frame-wrapper">
                {isPreviewLoading && (
                  <div className="mapdoc-overlay">
                    <$Skeleton active />
                  </div>
                )}

                {docType === "pdf" ? (
                  <iframe
                    className="case-doc-frame frame-pdf"
                    src={finalUrl}
                    title={assignedDocumentContent?.data?.documentName}
                    style={{ width: "100%", height: "100%", border: 0 }}
                    onLoad={() => setIsPreviewLoading(false)}
                  />
                ) : (
                  <object
                    className="case-doc-frame frame-html"
                    type={mimeType}
                    data={finalUrl}
                    onLoad={() => setIsPreviewLoading(false)}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="case-doc-viwer-inner mapdoc-skeleton-top ">
              <$Skeleton active />
            </div>
          ))}

        <div className="drawer-footer-fixed align-content-center justify-content-end">
          <div>
            <$Button onClick={() => onClose()}>
              {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
            </$Button>
          </div>
        </div>
      </$Skeleton>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { common, documentsManagerHistory } = state;
  const { currentDateFormat, currentLanguage } = common;
  const { assignedDocumentContent } = documentsManagerHistory;

  return {
    currentDateFormat,
    currentLanguage,
    assignedDocumentContent,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AssignDocContent);
